import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Table, Card, Row, Col, Badge, Button, Spinner, Modal, Form, Pagination, InputGroup, FormControl, DropdownButton, Dropdown } from 'react-bootstrap';
import Navbarpro from './sidebar';
import { api } from '../../../config';

function Customer() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      // Try new admin endpoint first
      let data;
      try {
        const res = await api.get('/admin/vendors');
        data = res.data.vendors || [];
      } catch {
        // Fallback to legacy endpoint
        const res = await api.get('/vendor');
        data = res.data || [];
      }
      setVendors(data);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  // Filter vendors
  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = !searchTerm ||
      vendor.Business_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.Owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.Email_address?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !categoryFilter || vendor.Category === categoryFilter;
    const matchesStatus = !statusFilter || vendor.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredVendors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);

  // Stats
  const stats = {
    total: vendors.length,
    technical: vendors.filter(v => v.Category === 'Technical').length,
    nonTechnical: vendors.filter(v => v.Category === 'Non-Technical').length,
    products: vendors.filter(v => v.Category === 'Products').length
  };

  const getStatusBadge = (status) => {
    const colors = { active: 'success', suspended: 'warning', banned: 'danger' };
    return <Badge bg={colors[status] || 'success'}>{status || 'Active'}</Badge>;
  };

  const getKYCBadge = (status) => {
    return <span className={`kyc-${status || 'pending'}`}>{status || 'Pending'}</span>;
  };

  if (loading) {
    return (
      <div className="loading-state">
        <Navbarpro />
        <div className="text-center" style={{ marginTop: '20vh' }}>
          <Spinner animation="border" />
          <p className="mt-3">Loading vendors...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbarpro />

      <Container fluid className="py-4 px-4">
        {/* Page Header */}
        <div className="page-header d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">
              <i className="bi bi-shop-window me-2" style={{ color: 'var(--primary)' }}></i>
              Vendor Management
            </h4>
            <p className="text-secondary mb-0">View and manage all approved vendors</p>
          </div>
          <div className="d-flex gap-2">
            <Link to="/pending" className="btn btn-outline-warning btn-sm">
              <i className="bi bi-clock me-1"></i> Pending
            </Link>
            <Link to="/vendor" className="btn btn-primary btn-sm">
              <i className="bi bi-plus me-1"></i> Add Vendor
            </Link>
          </div>
        </div>

        {/* Alerts */}
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Stats */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <div className="stat-card">
              <div className="d-flex align-items-center gap-3">
                <div className="icon">
                  <i className="bi bi-people"></i>
                </div>
                <div>
                  <div className="value">{stats.total}</div>
                  <div className="label">Total Vendors</div>
                </div>
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-card">
              <div className="d-flex align-items-center gap-3">
                <div className="icon" style={{ background: 'rgba(0, 212, 255, 0.1)', color: 'var(--accent-cyan)' }}>
                  <i className="bi bi-tools"></i>
                </div>
                <div>
                  <div className="value">{stats.technical}</div>
                  <div className="label">Technical</div>
                </div>
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-card">
              <div className="d-flex align-items-center gap-3">
                <div className="icon" style={{ background: 'rgba(0, 200, 83, 0.1)', color: 'var(--accent-green)' }}>
                  <i className="bi bi-person-workspace"></i>
                </div>
                <div>
                  <div className="value">{stats.nonTechnical}</div>
                  <div className="label">Non-Technical</div>
                </div>
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-card">
              <div className="d-flex align-items-center gap-3">
                <div className="icon" style={{ background: 'rgba(124, 77, 255, 0.1)', color: 'var(--accent-purple)' }}>
                  <i className="bi bi-box-seam"></i>
                </div>
                <div>
                  <div className="value">{stats.products}</div>
                  <div className="label">Products</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center g-3">
              <Col md={4}>
                <InputGroup>
                  <FormControl
                    placeholder="Search vendors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="outline-secondary">
                    <i className="bi bi-search"></i>
                  </Button>
                </InputGroup>
              </Col>
              <Col md={3}>
                <DropdownButton
                  title={categoryFilter || "All Categories"}
                  variant="outline-secondary"
                  onSelect={(e) => setCategoryFilter(e)}
                  className="w-100"
                >
                  <Dropdown.Item eventKey="">All Categories</Dropdown.Item>
                  <Dropdown.Item eventKey="Technical">Technical</Dropdown.Item>
                  <Dropdown.Item eventKey="Non-Technical">Non-Technical</Dropdown.Item>
                  <Dropdown.Item eventKey="Products">Products</Dropdown.Item>
                </DropdownButton>
              </Col>
              <Col md={3}>
                <DropdownButton
                  title={statusFilter || "All Status"}
                  variant="outline-secondary"
                  onSelect={(e) => setStatusFilter(e)}
                  className="w-100"
                >
                  <Dropdown.Item eventKey="">All Status</Dropdown.Item>
                  <Dropdown.Item eventKey="active">Active</Dropdown.Item>
                  <Dropdown.Item eventKey="suspended">Suspended</Dropdown.Item>
                  <Dropdown.Item eventKey="banned">Banned</Dropdown.Item>
                </DropdownButton>
              </Col>
              <Col md={2} className="text-end">
                <span className="text-secondary">{filteredVendors.length} vendors</span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Vendors Table */}
        {filteredVendors.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox"></i>
            <h5>No vendors found</h5>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="data-table-wrapper">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Business</th>
                  <th>Owner</th>
                  <th>Contact</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>KYC</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((vendor, idx) => (
                  <tr key={vendor._id}>
                    <td>{indexOfFirstItem + idx + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="vendor-avatar"
                          style={{ width: 36, height: 36, fontSize: '0.875rem' }}
                        >
                          {vendor.Business_Name?.charAt(0) || 'V'}
                        </div>
                        <div>
                          <div className="fw-medium">{vendor.Business_Name || 'N/A'}</div>
                          <small className="text-secondary">
                            {Array.isArray(vendor.Sub_Category)
                              ? vendor.Sub_Category[0]
                              : vendor.Sub_Category || 'N/A'}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{vendor.Owner_name || 'N/A'}</td>
                    <td>
                      <small>
                        <i className="bi bi-envelope me-1"></i>{vendor.Email_address || 'N/A'}
                        <br />
                        <i className="bi bi-phone me-1"></i>{vendor.Phone_number || 'N/A'}
                      </small>
                    </td>
                    <td>
                      <Badge bg={
                        vendor.Category === 'Technical' ? 'primary' :
                          vendor.Category === 'Non-Technical' ? 'success' : 'info'
                      }>
                        {vendor.Category || 'N/A'}
                      </Badge>
                    </td>
                    <td>{getStatusBadge(vendor.status)}</td>
                    <td>{getKYCBadge(vendor.kycStatus)}</td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/vendor/${vendor._id}`)}
                          title="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-4">
            <span className="text-secondary">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredVendors.length)} of {filteredVendors.length}
            </span>
            <Pagination>
              <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
              <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (pageNum > totalPages) return null;
                return (
                  <Pagination.Item key={pageNum} active={currentPage === pageNum} onClick={() => setCurrentPage(pageNum)}>
                    {pageNum}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
              <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </Container>
    </div>
  );
}

export default Customer;
